import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { motion } from 'framer-motion';

import { interactiveHoverVariants } from '@/animations';
import { APP_CONFIG } from '@/config';
import { BREADCRUMB_CONTENT } from '@/content';
import { BreadcrumbJsonLd } from '@/seo';
import { Icon } from '@/utils/icons';

interface BreadcrumbProps {
  items?: { label: string; path?: string }[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  const location = useLocation();

  const breadcrumbItems = useMemo(() => {
    if (items) return items;

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: { label: string; path?: string }[] = [
      { label: BREADCRUMB_CONTENT.home, path: '/' },
    ];

    pathSegments.forEach((segment, index) => {
      const path = `/${pathSegments.slice(0, index + 1).join('/')}`;
      const isLast = index === pathSegments.length - 1;
      breadcrumbs.push({
        label: segment.replace(/-/g, ' '),
        path: isLast ? undefined : path,
      });
    });

    return breadcrumbs;
  }, [items, location.pathname]);

  const BASE_URL = APP_CONFIG.url.endsWith('/') ? APP_CONFIG.url.slice(0, -1) : APP_CONFIG.url;

  return (
    <>
      <BreadcrumbJsonLd
        items={breadcrumbItems.map(item => ({
          name: item.label,
          url: `${BASE_URL}${item.path || location.pathname}`,
        }))}
      />
      <div className="to-primary-50 border-primary-100/80 from-primary-50/50 border-b bg-gradient-to-r">
        <div className="container-toys py-2.5">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2">
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={`${item.path || 'last'}-${item.label}`}>
                {index > 0 && (
                  <Icon
                    name="ChevronRight"
                    size={16}
                    className="text-primary-300"
                    aria-hidden="true"
                  />
                )}
                {item.path ? (
                  <motion.div variants={interactiveHoverVariants} whileHover="hover" whileTap="tap">
                    <Link
                      to={item.path}
                      className="font-body hover:text-primary-600 flex items-center gap-2 text-sm font-semibold text-neutral-600 transition-all duration-200">
                      {index === 0 && <Icon name="Home" size={14} className="text-primary-500" />}
                      <span className="capitalize">{item.label}</span>
                    </Link>
                  </motion.div>
                ) : (
                  <span
                    className="font-body text-primary-600 text-sm font-bold capitalize"
                    aria-current="page">
                    {item.label}
                  </span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Breadcrumb;
